import { Injectable } from '@angular/core';
import { addWeeks, formatDistanceToNowStrict, getUnixTime, isPast, parseISO } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject, combineLatest, defer, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, first, map, tap, shareReplay, switchMap, catchError } from 'rxjs/operators';

import { CachingService } from '../caching.service';
import { ConfigService } from '../config.service';
import { FwLegacyDialogService as LegacyDialogService } from '../dialogs/dialog.service';
import { FwDialogService } from '@flywheel-io/vision';
import { ExpiredApiKeysDialogComponent, IGNORE_EXPIRATION_KEY } from './expired-api-keys-dialog/expired-api-keys-dialog.component';
import { ExpiredApiKey } from './expired-api-keys-dialog/expired-api-keys-dialog.component';
import { CoreApiService, queryAll, WriteResult } from '../core-api.service';
import { LoggingService } from '../logging.service';
import { BrowserWarningComponent } from '../login/browser-warning.component';
import { NotificationService } from '../notification.service';
import { defaultServerSettings, defaultSessionSettings, getLocalStorageSettings, USER_SETTINGS_KEY } from './setting.utils';
import { ContainerAccess } from '../shared/enums/container-access.enum';
import { RequestState } from '../shared/enums/request-state.enum';
import { SiteRole } from '../shared/enums/site-role.enum';
import { ApiKey } from '../shared/models/api-key.model';
import { Collection } from '../shared/models/collection.model';
import { Device } from '../shared/models/device.model';
import { Group } from '../shared/models/group.model';
import { MultifactorSettings, Profile } from '../shared/models/profile.model';
import { UserSettings } from '../shared/models/user-settings.model';
import { UserPreferences } from '../shared/models/user.model';

@Injectable({
  providedIn: 'root',
})
export class ProfileService extends CachingService {
  private hasWarnedForExpiringKeys = false

  private profile = {
    state: RequestState.Empty,
    subject: new BehaviorSubject<Profile | undefined>(undefined),
    request: combineLatest([
        this.coreApi.users.self() as Observable<Profile>,
        this.mfaSettings(),
      ]).pipe(
        map(([profile, multifactor]) => {
          if (multifactor) {
            profile.multifactor = multifactor[0] || {};
          }
          return profile;
        }),
      ),
  }
  readonly profile$ = this.profile.subject.pipe(
    tap(user => {
      // associate errors to the current user
      // TODO: add back sentry user when we turn sentry back on after angular update
      // Sentry.setUser({ id: user?.user_id });
      // set userpilot user data
      const { userpilot } = window as any; // eslint-disable-line @typescript-eslint/no-explicit-any
      if (userpilot) {
        this.config.getVersion().pipe(first()).subscribe(version => {
          if (user) {
            userpilot.identify(user.email, {
              roles: user.roles,
              version: version?.flywheel_release,
            });
          } else {
            userpilot.anonymous({ version: version?.flywheel_release });
          }
        });
      }
      if (user?.preferences) {
        const updatedSettings: UserSettings = { ...this.settings.value, ...user.preferences };
        if (!isEqual(updatedSettings, this.settings.value)) {
          this.settings.next(updatedSettings);
        }
      }
    }),
    shareReplay(1),
  )

  private settings = new BehaviorSubject<UserSettings>(getLocalStorageSettings())
  readonly settings$ = this.settings.asObservable()

  readonly siteAdmin$ = defer(() => this.getProfile()).pipe(
    distinctUntilChanged(),
    filter(profile => Boolean(profile)), // wait until we have a user established
    map(profile => SiteRole.isSiteAdmin(profile)),
  )
  // Emits true if the user is an admin with "showAllData" enabled
  readonly exhaustiveMode$: Observable<boolean> = combineLatest([
    this.siteAdmin$,
    this.settings$,
  ]).pipe(
    map(([isSiteAdmin, settings]) => isSiteAdmin && settings.showAllData),
    distinctUntilChanged(),
  )

  private userTaskCount = {
    state: RequestState.Empty,
    subject: new BehaviorSubject<number>(undefined),
    request: defer(() => this.getProfile()).pipe(
      switchMap(profile => profile ? this.coreApi.readerTasks.query({
        filter: `assignee=${profile._id},status=|[Todo,In_progress]`,
      }).pipe(map(({ total }) => total)) : of(0)),
    ),
  }
  readonly userTaskCount$ = this.userTaskCount.subject.asObservable()
  private expiredKeysDialogIsOpen = false

  constructor(
    private config: ConfigService,
    private legacyDialog: LegacyDialogService,
    private dialog: FwDialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
  ) {
    super();

    this.checkBrowserCompatability();
  }

  set showAllData(value: boolean) {
    this.updateSettings({ showAllData: value });
  }

  mfaSettings(): Observable<MultifactorSettings> {
    if (this.config.isFeatureEnabled('multifactor_auth')) {
      return this.coreApi.users.mfaSettings.get().pipe(
        catchError(error => {
          if (error.status === 404) {
            return of(undefined);
          }
          throw error;
        }),
      );
    } else {
      return of(undefined);
    }
  }

  openExpiringKeysDialog(): void {
    combineLatest([
      this.profile$.pipe(
        filter(profile => Boolean(profile)),
      ),
      queryAll(this.coreApi.devices.query, { join_keys: true }).pipe(
        catchError(() => {
          return of([]) // ignore 403 errors
        })
      ) as Observable<Device[]>,
    ]).pipe(first())
    .subscribe(([profile, devices]) => {
      if (this.hasWarnedForExpiringKeys) {
        return;
      }

      const expiringUserKeys = this.checkForExpiringKeys(profile?.api_keys);
      let expiringDeviceKeys: ExpiredApiKey[] = [];
      for (const device of devices) {
        const myDeviceKeys = this.checkForExpiringKeys(device?.api_keys);
        if (myDeviceKeys) {
          expiringDeviceKeys = [...expiringDeviceKeys, ...myDeviceKeys];
        }
      }

      if (!this.expiredKeysDialogIsOpen && (expiringUserKeys?.length > 0 || expiringDeviceKeys?.length > 0)) {
        this.expiredKeysDialogIsOpen = true;
        this.dialog.open(ExpiredApiKeysDialogComponent, {
          data: {
            userKeys: expiringUserKeys,
            deviceKeys: expiringDeviceKeys,
          },
          disableClose: true,
        });
        this.hasWarnedForExpiringKeys = true;
      }
    });
  }

  checkForExpiringKeys(keys: ApiKey[]): ExpiredApiKey[] {
    if (!keys || keys?.length === 0) {
      return [];
    }

    const ignoredKeys: string[] = JSON.parse(localStorage.getItem(IGNORE_EXPIRATION_KEY)) || [];
    const expiringKeys: ExpiredApiKey[] = [];
    for (const key of keys) {
      const expireDate = key.expires_at ? parseISO(key.expires_at) : null;
      const isExpired = isPast(expireDate);
      const expiresSoon = expireDate ? (isExpired || (getUnixTime(expireDate) - getUnixTime(addWeeks(new Date(), 1)) < 0)) : false;
      if (!ignoredKeys.includes(key.key) && (isExpired || expiresSoon)) {
        expiringKeys.push({
          label: key.label,
          key: key.key,
          isExpired: isExpired,
          formattedDate: expireDate ? formatDistanceToNowStrict(expireDate) : null,
        } as ExpiredApiKey);
      }
    }
    return expiringKeys;
  }

  checkBrowserCompatability(): void {
    if (!this.settings.value.dismissBrowserWarning) {
      const browserInfo = navigator.userAgent.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
      const isChrome = /chrome/i.test(browserInfo[1]);
      // include special case for Edge because it tries to pretend it's Chrome.
      const promise = !isChrome || navigator.userAgent.includes('Edge')
        ? this.legacyDialog.open(BrowserWarningComponent, { width: '500px', disableClose: false }).afterClosed().toPromise()
        : Promise.resolve();
      promise.then(() => {
        this.updateSettings({ dismissBrowserWarning: true });
      });
    }
  }

  updateSettings(updates: Partial<UserSettings>): void {
    const settings = { ...this.settings.value, ...updates };
    if (!isEqual(settings, this.settings.value)) {
      this.saveSettings(settings);
      this.settings.next(settings);
    }
  }

  getProfile({ refresh = false } = {}): Observable<Profile | undefined> {
    return this.getCacheableResource(this.profile, this.profile$, { refresh });
  }

  // Many AngularJS controllers just need one time promises that ensure they're getting a fresh profile without cleaning up a subscription.
  getProfilePromise(): Promise<Profile> {
    // Due to how .toPromise works, we need first in the pipe
    // https://github.com/ReactiveX/rxjs/issues/2536#issuecomment-292829380
    return this.getProfile().pipe(first(data => Boolean(data))).toPromise();
  }

  getUserTaskCount({ refresh = false } = {}): Observable<number> {
    return this.getCacheableResource(this.userTaskCount, this.userTaskCount$, { refresh });
  }

  generateApiKey(): Observable<string> {
    return this.coreApi.users.apiKeys.generateKey().pipe(
      map(({ key }) => key)
    );
  }

  /**
   * Clear the profile state only. Called from AuthService.logout(), which also clears auth state.
   */
  logout(): void {
    this.profile.state = RequestState.Empty;
    this.profile.subject.next(undefined);
    this.updateSettings(defaultSessionSettings);
  }

  /**
   * @deprecated use granular permission checks
   */
  canAdminContainer(container: Collection | Group): boolean {
    if (!this.profile.subject.value) {
      return false;
    }
    if (this.canManage()) {
      return true;
    }
    return (container?.permissions ?? []).some(permission =>
      permission._id === this.profile.subject.value._id && permission.access === ContainerAccess.Admin);
  }

  /**
   * @deprecated use SiteRole.isSiteAdmin
   */
  canManage(): boolean {
    return this.profile.subject.value && SiteRole.isSiteAdmin(this.profile.subject.value);
  }

  /**
   * @deprecated use granular permission checks
   */
  canWriteToGroup(group: Group): boolean {
    if (!this.profile.subject.value) {
      return false;
    }
    if (this.canManage()) {
      return true;
    }
    if (!group || !group.permissions || !group.permissions.length) {
      return false;
    }
    return group.permissions.some(function (role) {
      return role._id === this.profile.subject.value._id && [ContainerAccess.ReadWrite, ContainerAccess.Admin].includes(role.access);
    }, this);
  }

  getAvatarType(): string {
    for (const [key, value] of Object.entries(this.profile.subject.value.avatars)){
      if (this.profile.subject.value.avatar === value) { return key; }
    }
    return '';
  }

  putProfile(data): Observable<WriteResult> {
    return this.coreApi.users.put(this.profile.subject.value._id, data).pipe(tap(() => {
      this.getProfile({ refresh: true });
    }));
  }

  saveSettings(settings: UserSettings): void {
    const sessionSettings: Partial<UserSettings> = {};
    const localSettings: Partial<UserSettings> = {};
    const serverSettings: Partial<UserPreferences> = {};
    for (const key in settings) {
      if (key in defaultSessionSettings) {
        sessionSettings[key] = settings[key];
      } else if (key in defaultServerSettings) {
        serverSettings[key] = settings[key];
      } else {
        localSettings[key] = settings[key];
      }
    }
    sessionStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(sessionSettings));
    localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(localSettings));
    const lastServerSettings: Partial<UserSettings> = {};
    for (const key in this.settings.value) {
      if (key in defaultServerSettings) {
        lastServerSettings[key] = this.settings.value[key];
      }
    }
    if (!isEqual(lastServerSettings, serverSettings)) {
      this.coreApi.users.updatePreferences(serverSettings as UserPreferences).subscribe({
        error: (error) => {
          this.notifications.error('profile.updatePreferences.error');
          this.logger.error(error);
        },
      });
    }
  }
}
